import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';

import { UserAccountRole, UserLocationRole } from '@shared/user/enums';
import { AuthUser } from '@shared/signup/models';
import { PermissionService } from '@tablesready/frontend-permissions';
import { combineLatest, concatMap, filter, map, of, Subscription, tap } from 'rxjs';
import { AuthRestaurantService, LocationsService } from '@tablesready/frontend-store';
import { AppIcon } from '@tablesready/frontend-icons';
import { NavComponent } from './frontend-settings-nav.interfaces';
import {
  BillingVisibility,
  FrontendPaidFeatureService,
} from '@tablesready/frontend-paid-feature-alert';

const managerReadPermission = 'manager:read';
const userAndInvites = 'Users & invites';
const organizationRoute = '/account/organization';

@Component({
  selector: 'frontend-settings-nav',
  templateUrl: './frontend-settings-nav.component.html',
  styleUrls: ['./frontend-settings-nav.component.scss'],
  standalone: false,
})
export class FrontendSettingsNavComponent implements OnInit, OnDestroy {
  @Output() itemClicked = new EventEmitter();
  user?: AuthUser;
  reservationTitle = '';
  reservationTitlePlural = '';
  countOfTotalLocations?: number;
  isLocationManager = true;
  settingsNavItems = [];

  private readonly subscription = new Subscription();

  constructor(
    private readonly permissionService: PermissionService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly paidService: FrontendPaidFeatureService,
    private readonly locationsService: LocationsService,
  ) {}

  linkClick(): void {
    this.itemClicked.emit();
  }

  ngOnInit(): void {
    const restSub = this.permissionService.userAccountHasScopesAsync(
      ['manager:read'],
      [UserAccountRole.user, UserAccountRole.admin],
    )
      .pipe(
        filter(data => !data),
        concatMap(() => this.authRestaurantService.select()),
      )
      .subscribe((user) => {
        this.update(user);
        this.buildNavItems(user);
      });
    this.subscription.add(restSub);

    const restaurantSub = combineLatest([
      this.locationsService.selectAll(),
      this.authRestaurantService.select(),
    ])
      .pipe(
        tap(([, user]) => {
          this.update(user);
        }),
        concatMap(([locations, user]) => {
          if (user.account && this.countOfTotalLocations === undefined) {
            return this.locationsService.load()
              .pipe(map((locations) => ({
                locations,
                user,
              })));
          }

          return of({ locations, user });
        }),
      )
      .subscribe(({ locations, user }) => {
        this.countOfTotalLocations = locations.length;
        this.buildNavItems(user);
      });
    this.subscription.add(restaurantSub);
  }

  update(user: AuthUser): void {
    this.user = user;
    this.reservationTitle = user.location?.displayOptions?.labels?.reservation || 'reservation';
    this.reservationTitlePlural = user.location?.displayOptions?.labels?.reservation_plural || 'reservations';
    this.isLocationManager = (user.accountUser && user.accountUser.role === UserAccountRole.user &&
      user.locationUser && user.locationUser.role === UserLocationRole.manager) || false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.locationsService.reset();
  }

  buildNavItems(user: AuthUser): void {
    const navComponents: NavComponent[] = [
      {
        href: '/account/profile',
        name: 'Preferences',
        class: 'dropdown',
        scopes: [],
        availableFor: [UserAccountRole.user, UserAccountRole.admin],
        subNav: [
          {
            href: '/preferences/notifications',
            name: 'Notifications',
            icon: AppIcon.notifications,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/waitlist',
            name: 'Waitlist Options',
            icon: AppIcon.waitlistOptions,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/reservation',
            name: `${this.reservationTitle} Options`,
            icon: AppIcon.reservationOptions,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/schedules',
            name: 'Schedules',
            icon: AppIcon.schedules,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/services',
            name: 'Services',
            icon: AppIcon.services,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
            visibility: BillingVisibility.BUSINESS_PLAN_OUTSIDE_US,
          },
          {
            href: '/preferences/resources',
            name: 'Resources & Table management',
            icon: AppIcon.resources,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
            visibility: BillingVisibility.BUSINESS_PLAN_OUTSIDE_US,
          },
          {
            href: '/preferences/public-pages',
            name: 'Public Pages',
            icon: AppIcon.publicPages,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
          },
          {
            href: '/preferences/party-fields',
            name: 'Custom properties',
            icon: AppIcon.partyFields,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
            visibility: BillingVisibility.BUSINESS_PLAN_OUTSIDE_US,
          },
          {
            href: '/preferences/automations',
            name: 'Automations',
            icon: AppIcon.automations,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
            visibility: BillingVisibility.BUSINESS_PLAN_OUTSIDE_US,
          },
          {
            href: '/preferences/integrations',
            name: 'Integrations',
            icon: AppIcon.integrations,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
            visibility: BillingVisibility.BUSINESS_PLAN_OUTSIDE_US,
          },
        ],
      }];

    if (!this.isLocationManager && this.countOfTotalLocations === 1) {
      navComponents.push({
        href: organizationRoute,
        name: 'Account',
        class: 'dropdown',
        scopes: [managerReadPermission],
        availableFor: [UserAccountRole.admin],
        subNav: [
          {
            href: '/location/billing',
            name: 'Subscription Plan',
            icon: AppIcon.plans,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.user, UserAccountRole.admin],
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
          {
            href: organizationRoute,
            name: 'Organization',
            icon: AppIcon.organization,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
          },
          {
            href: '/account/locations',
            name: 'Locations',
            icon: AppIcon.locations,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
          },
          {
            href: '/account/users',
            name: userAndInvites,
            icon: AppIcon.users,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
          },
          {
            href: '/account/billing',
            name: 'Billing',
            icon: AppIcon.billing,
            scopes: [managerReadPermission],
            availableFor: [UserAccountRole.admin],
            visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
          },
        ],
      });
    } else {
      navComponents.push(...[
        {
          href: '/location/info',
          name: 'Location management',
          class: 'dropdown',
          scopes: [managerReadPermission],
          availableFor: [UserAccountRole.user, UserAccountRole.admin],
          subNav: [
            {
              href: '/location/billing',
              name: 'Subscription Plan',
              icon: AppIcon.plans,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.user, UserAccountRole.admin],
              visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
            },
            {
              href: '/location/info',
              name: 'Location info',
              icon: AppIcon.location,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.user, UserAccountRole.admin],
            },
            {
              href: '/location/users',
              name: userAndInvites,
              icon: AppIcon.users,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.user, UserAccountRole.admin],
            },
          ],
        },
        {
          href: organizationRoute,
          name: 'Account',
          class: 'dropdown',
          scopes: [managerReadPermission],
          availableFor: [UserAccountRole.admin],
          subNav: [
            {
              href: organizationRoute,
              name: 'Organization',
              icon: AppIcon.organization,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
            },
            {
              href: '/account/locations',
              name: 'Locations',
              icon: AppIcon.locations,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
            },
            {
              href: '/account/users',
              name: userAndInvites,
              icon: AppIcon.users,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
            },
            {
              href: '/account/billing',
              name: 'Billing',
              icon: AppIcon.billing,
              scopes: [managerReadPermission],
              availableFor: [UserAccountRole.admin],
              visibility: BillingVisibility.LINKS_ONLY_ON_MOBILE,
            },
          ],
        },
      ]);
    }

    this.settingsNavItems = navComponents.map((parentNav) => {
      if (!user) {
        return {
          href: '',
          name: '',
          scopes: [],
          subNav: undefined,
        };
      }

      const { scopes, availableFor, subNav } = parentNav;
      const isParentNavHasScope = user.accountUser
        ? this.permissionService.userAccountHasScopes(user, scopes, availableFor)
        : this.permissionService.hasScopes(user, scopes);

      if (isParentNavHasScope) {
        const subNavs = subNav.filter((item) => {
          if (
            this.paidService.billingsVisibility === BillingVisibility.HIDDEN_ON_MOBILE &&
            (item.visibility === BillingVisibility.LINKS_ONLY_ON_MOBILE ||
            item.visibility === BillingVisibility.BUSINESS_PLAN_OUTSIDE_US ||
              item.visibility === BillingVisibility.STARTER_PLAN_OUTSIDE_US)
          ) {
            return false;
          }

          if (
            this.paidService.billingsVisibility === BillingVisibility.STARTER_PLAN_OUTSIDE_US &&
            item.visibility === BillingVisibility.BUSINESS_PLAN_OUTSIDE_US
          ) {
            return false;
          }

          if (
            this.paidService.billingsVisibility === BillingVisibility.STARTER_PLAN_OUTSIDE_US &&
            item.visibility === BillingVisibility.LINKS_ONLY_ON_MOBILE
          ) {
            return false;
          }

          if (
            this.paidService.billingsVisibility === BillingVisibility.BUSINESS_PLAN_OUTSIDE_US &&
            item.visibility === BillingVisibility.LINKS_ONLY_ON_MOBILE
          ) {
            return false;
          }

          return this.permissionService
            .userAccountHasScopes(user, item.scopes, item.availableFor);
        });

        return {
          href: parentNav.href,
          name: parentNav.name,
          scopes: parentNav.scopes,
          subNav: subNavs,
        };
      }

      return {
        href: '',
        name: '',
        scopes: [],
        subNav: undefined,
      };
    }).filter(elem => elem !== null);
  }
}
